import { SidUserClient } from "@sid/sid_client";
import { BaseInfo, CommonAttrInfo, LinkageUserData, ManageInfo, MemberAgreementInfo, ShippingInfo, SiteInfo, UserBase, UserShueishaMangasai } from "@/commons/interfaces";
import { InjectionKey, reactive } from "vue";
import { LinkageUserItem } from "@/modules/linkage/linkageUserItem";
import ShueishaMangasaiProfile from "@/components/Linkage/Sites/ShueishaMangasaiProfile.vue";
import ShueishaMangasaiConfirm from "@/components/Linkage/Sites/ShueishaMangasaiConfirm.vue";
import ShueishaMangasaiLoginCustom from "@/components/Linkage/Sites/ShueishaMangasaiLoginCustom.vue";

// 集英社書籍関連定数定義
/** 連携ID */
export const SHUEISHA_MANGASAI_LINKAGE_ID = 6;
/** 連携名称 */
export const SHUEISHA_MANGASAI_LINKAGE_NAME = "ShueishaMangasai";
/** 連携パス */
export const SHUEISHA_MANGASAI_PATH = "shueisha_mangasai";
/** サイト固有情報APIパス */
export const SHUEISHA_MANGASAI_SITE_UNIQ_API_PATH = "/shueisha_mangasai/me";
/** デフォルトコールバックURL */
export const SHUEISHA_MANGASAI_DEFAULT_CALLBACK_URL = process.env.VUE_APP_SHUEISHA_MANGASAI_DEFAULT_CALLBACK_URL;
/** 許容コールバックURL */
export const SHUEISHA_MANGASAI_ACCEPT_CALLBACK_URLS = process.env.VUE_APP_SHUEISHA_MANGASAI_ACCEPT_CALLBACK_URLS.split(",").map((url: string) => `^${url}(\\?.*)?$`);
/** サービス規約URL */
export const SHUEISHA_MANGASAI_SERVICE_TERM_URL = process.env.VUE_APP_SHUEISHA_MANGASAI_SERVICE_TERM_URL;
/** メールアドレスドメイン */
export const SHUEISHA_MANGASAI_EMAIL_DOMAINS = [
    "mangasai.sid.shueisha.co.jp",
];
/** 基本属性情報入力 */
export const SHUEISHA_MANGASAI_PROFILE_COMPONENT = ShueishaMangasaiProfile;
/** 基本属性情報確認 */
export const SHUEISHA_MANGASAI_CONFIRM_COMPONENT = ShueishaMangasaiConfirm;
/** コールバックURLの任意指定 */
export const SHUEISHA_MANGASAI_IS_CALLBACK_OPTIONAL = true;

/**
 * 集英社マンガ祭連携画面用基本属性情報管理クラス
 */
export class ShueishaMangasaiUserBase implements LinkageUserData<UserBase> {

    /** インジェクションキー */
    public static injectionKey: InjectionKey<LinkageUserItem<LinkageUserData<UserBase>>> = Symbol("Shueisha_MangasaiUserBase");

    /** 基本属性情報 */
    public readonly data: UserBase = reactive(new UserBase());
    /**
     * @inheritDoc LinkageUserData
     */
    public initData(): void {
        this.copy(new UserBase());
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public getData(): UserBase {
        return this.data;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public copy(obj: UserBase): void {
        this.data.identityId = obj.identityId;
        this.data.userName = obj.userName;
        this.data.baseInfo = Object.assign(new BaseInfo(), obj.baseInfo);
        this.data.shippingInfo = obj.shippingInfo?.map(inf => Object.assign(new ShippingInfo(), inf)) || [];
        this.data.siteInfo = obj.siteInfo?.map(inf => Object.assign(new SiteInfo(), inf)) || [];
        this.data.commonAttrInfo = Object.assign(new CommonAttrInfo(), obj.commonAttrInfo);
        this.data.memberAgreeInfo = Object.assign(new MemberAgreementInfo(), obj.memberAgreeInfo);
        this.data.manageInfo = Object.assign(new ManageInfo(), obj.manageInfo);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public async fetch(sidClient: SidUserClient): Promise<void> {
        const response = await sidClient.getUserAttr()
            .then(res => res.data)
            .catch(res => {
                if (res.response?.status === 404) {
                    return new UserBase();
                } else {
                    throw new Error("Failed to get information.");
                }
            });
        this.copy(response);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public checkRequired(): boolean {
        // 基本属性情報の有無
        if (!this.data.identityId) return false;
        // 登録者情報
        if (!this.data.baseInfo.email) return false;

        // 属性情報
        if (!this.data.commonAttrInfo.gender) return false;
        if (!this.data.commonAttrInfo.birthday) return false;

        return true;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    getInjectKey(): InjectionKey<LinkageUserItem<LinkageUserData<UserBase>>> {
        return ShueishaMangasaiUserBase.injectionKey;
    }

}

/**
 * 集英社マンガ祭連携画面用サイト固有情報管理クラス
 */
export class ShueishaMangasaiSiteUniq implements LinkageUserData<UserShueishaMangasai> {

    /** インジェクションキー */
    public static injectionKey: InjectionKey<LinkageUserItem<LinkageUserData<UserShueishaMangasai>>> = Symbol("UserShueishaMangasai");

    /** 集英社マンガ祭サイト固有情報 */
    public readonly data: UserShueishaMangasai = reactive(new UserShueishaMangasai());

    /**
     * @inheritDoc LinkageUserData
     */
    public initData(): void {
        this.copy(new UserShueishaMangasai());
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public getData(): UserShueishaMangasai {
        return this.data;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public copy(obj: UserShueishaMangasai): void {
        this.data.identityId = obj.identityId;
        this.data.userName = obj.userName;
        this.data.isSendMailMagazine = obj.isSendMailMagazine;
        this.data.manageInfo = Object.assign({}, obj.manageInfo);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public async fetch(sidClient: SidUserClient): Promise<void> {
        const response = await sidClient.requestApi({ method: "GET", path: SHUEISHA_MANGASAI_SITE_UNIQ_API_PATH })
            .then(res => res.data)
            .catch(res => {
                if (res.response?.status === 404) {
                    return new UserShueishaMangasai();
                } else {
                    throw new Error("Failed to get information.");
                }
            });
        this.copy(response);
    }

    /**
     * @inheritDoc LinkageUserData
     */
    public checkRequired(): boolean {
        // サイト固有情報の有無
        if (this.data.isSendMailMagazine === null || this.data.isSendMailMagazine === undefined) return false;
        return true;
    }

    /**
     * @inheritDoc LinkageUserData
     */
    getInjectKey(): InjectionKey<LinkageUserItem<LinkageUserData<UserShueishaMangasai>>> {
        return ShueishaMangasaiSiteUniq.injectionKey;
    }
}